import { flattenToc, parseCfi } from '@mhe/reader/utils';
import { BonsaiTree, TagLocation, TagNode, TagType, TocItem } from '@mhe/reader/models';
import { TopicResolutionHints } from '../topics-metadata.resolver';

export type TopicTocMatchType = 'anchor' | 'spine-title';

export interface TopicTocCandidate {
  tocId: string | null
  tocLabel: string
  spinePos: number
  spineItemId: string
  matchType: TopicTocMatchType
  score: number
}

export interface TopicTocReverseMapEntry {
  subjectId?: string
  subjectTitle?: string
  categoryId?: string
  categoryTitle?: string
  topicId: string
  topicTitle: string
  location: TagLocation
  parsedCfi: {
    spinePos: number
    spineId: string | null
    elementId?: string | null
    ancestorIds?: string[]
    stepIndexes?: number[]
  }
  pageScopeKey: string
  bestCandidate?: TopicTocCandidate
  candidates: TopicTocCandidate[]
}

export interface TopicTocReverseMap {
  entries: TopicTocReverseMapEntry[]
  byAnchorUuid: Record<string, TopicTocReverseMapEntry[]>
  bySpineId: Record<string, TopicTocReverseMapEntry[]>
  bySpinePos: Record<number, TopicTocReverseMapEntry[]>
  byPageScopeKey: Record<string, TopicTocReverseMapEntry[]>
  byTocId: Record<string, TopicTocReverseMapEntry[]>
}

export const EMPTY_TOPIC_TOC_REVERSE_MAP: TopicTocReverseMap = {
  entries: [],
  byAnchorUuid: {},
  bySpineId: {},
  bySpinePos: {},
  byPageScopeKey: {},
  byTocId: {},
};

export function buildTopicTocReverseMap(
  topicsTree: BonsaiTree<TagNode | Record<string, unknown>>,
  toc: TocItem[],
): TopicTocReverseMap {
  const flatToc = flattenToc(toc || []);
  const entries = collectTopicEntries(topicsTree, flatToc);

  return {
    entries,
    byAnchorUuid: indexEntries(entries, (entry) => entry.location.anchorUuid),
    bySpineId: indexEntries(entries, (entry) => entry.parsedCfi.spineId || entry.location.spineId || undefined),
    bySpinePos: indexEntries(entries, (entry) => entry.parsedCfi.spinePos),
    byPageScopeKey: indexEntries(entries, (entry) => entry.pageScopeKey),
    byTocId: indexEntries(entries, (entry) => entry.bestCandidate?.tocId || undefined),
  };
}

export function derivePageScopeKey(cfi?: string, explicitSpineId?: string | null): string | null {
  if (!cfi && !explicitSpineId) {
    return null;
  }

  const parsed = cfi ? parseCfi(cfi) : undefined;
  const spineId = explicitSpineId || parsed?.spineId || null;
  const ancestorIds = (parsed?.steps || [])
    .map((step) => step.id)
    .filter((id): id is string => Boolean(id));

  // Use first two stable id-bearing containers as page scope.
  // This makes topic-heading CFIs and annotation CFIs on the same rendered page collide.
  const pageContainerPath = ancestorIds.slice(0, 2).join('/');
  const spineScope = spineId || `spine-pos-${parsed?.spinePos ?? 'unknown'}`;

  if (pageContainerPath) {
    return `${spineScope}::${pageContainerPath}`;
  }

  return `${spineScope}::root`;
}

export function findTopicTocReverseMapEntriesForPage(
  reverseMap: TopicTocReverseMap | undefined,
  selectedLocation: TagLocation | null,
  currentSpineId: string,
  hints?: TopicResolutionHints,
): TopicTocReverseMapEntry[] {
  if (!reverseMap?.entries?.length) {
    return [];
  }

  const targetPageScopeKey =
    derivePageScopeKey(selectedLocation?.cfi, selectedLocation?.spineId || currentSpineId) ||
    derivePageScopeKey(hints?.targetCfi, hints?.targetSpineId || currentSpineId) ||
    derivePageScopeKey(undefined, hints?.targetSpineId || selectedLocation?.spineId || currentSpineId);

  if (targetPageScopeKey) {
    const pageEntries = reverseMap.byPageScopeKey[targetPageScopeKey] || [];
    if (pageEntries.length > 0) {
      return pageEntries;
    }
  }

  if (selectedLocation?.anchorUuid) {
    const bySelectedAnchor = reverseMap.byAnchorUuid[selectedLocation.anchorUuid] || [];
    if (bySelectedAnchor.length > 0) {
      return bySelectedAnchor;
    }
  }

  if (hints?.targetAnchorUuid) {
    const byHintAnchor = reverseMap.byAnchorUuid[hints.targetAnchorUuid] || [];
    if (byHintAnchor.length > 0) {
      return byHintAnchor;
    }

    const byTocId = reverseMap.byTocId[hints.targetAnchorUuid] || [];
    if (byTocId.length > 0) {
      return byTocId;
    }
  }

  const spineId = hints?.targetSpineId || selectedLocation?.spineId || currentSpineId;
  if (spineId) {
    return findStrengthenedSpineFallbackEntries(
      reverseMap,
      spineId,
      selectedLocation?.cfi || hints?.targetCfi,
    );
  }

  return [];
}

export function findTopicTocReverseMapEntry(
  reverseMap: TopicTocReverseMap | undefined,
  selectedLocation: TagLocation | null,
  currentSpineId: string,
  hints?: TopicResolutionHints,
): TopicTocReverseMapEntry | null {
  const pageEntries = findTopicTocReverseMapEntriesForPage(
    reverseMap,
    selectedLocation,
    currentSpineId,
    hints,
  );

  return pickBestEntry(pageEntries, selectedLocation?.title || hints?.targetTitle);
}

export function summarizeTopicTocReverseMap(reverseMap: TopicTocReverseMap): Record<string, number> {
  return reverseMap.entries.reduce<Record<string, number>>((summary, entry) => {
    const matchType = entry.bestCandidate?.matchType || 'unmatched';
    summary[matchType] = (summary[matchType] || 0) + 1;
    return summary;
  }, {});
}

function collectTopicEntries(
  topicsTree: BonsaiTree<TagNode | Record<string, unknown>>,
  flatToc: TocItem[],
): TopicTocReverseMapEntry[] {
  const nodes = Object.values(topicsTree).filter(
    (node): node is TagNode => node && typeof node === 'object' && 'locations' in node,
  );

  return nodes.flatMap((topicNode) => {
    if (!topicNode.locations?.length) {
      return [];
    }

    const categoryNode = findNearestCategoryNode(topicsTree, topicNode);
    const subjectNode = categoryNode ? findParentSubjectNode(topicsTree, categoryNode) : undefined;

    return topicNode.locations.map((location) => {
      const parsed = parseCfi(location.cfi);
      const candidates = buildCandidates(flatToc, location, topicNode, categoryNode, parsed.spinePos);
      const ancestorIds = (parsed.steps || [])
        .map((step) => step.id)
        .filter((id): id is string => Boolean(id));
      const stepIndexes = (parsed.steps || []).map((step) => step.index);
      const pageScopeKey =
        derivePageScopeKey(location.cfi, location.spineId) ||
        `${location.spineId || 'unknown'}::root`;

      return {
        subjectId: subjectNode?.id,
        subjectTitle: subjectNode?.title || subjectNode?.name,
        categoryId: categoryNode?.id,
        categoryTitle: categoryNode?.title || categoryNode?.name,
        topicId: topicNode.id,
        topicTitle: topicNode.title || topicNode.name,
        location,
        parsedCfi: {
          spinePos: parsed.spinePos,
          spineId: parsed.spineId ?? null,
          elementId: parsed.steps?.find((step) => Boolean(step.id))?.id || null,
          ancestorIds,
          stepIndexes,
        },
        pageScopeKey,
        bestCandidate: candidates[0],
        candidates,
      };
    });
  });
}

function buildCandidates(
  flatToc: TocItem[],
  location: TagLocation,
  topicNode: TagNode,
  categoryNode: TagNode | undefined,
  locationSpinePos: number,
): TopicTocCandidate[] {
  const candidateMap = new Map<string, TopicTocCandidate>();
  const normalizedTopicTitle = normalizeText(topicNode.title || topicNode.name || location.title);
  const normalizedCategoryTitle = normalizeText(categoryNode?.title || categoryNode?.name || '');
  const normalizedLocationTitle = normalizeText(location.title || '');

  for (const tocItem of flatToc) {
    const tocId = tocItem.id;
    const tocLabel = tocItem.label || '';
    const normalizedTocLabel = normalizeText(tocLabel);
    const sameSpineId = tocItem.spineItem?.id === location.spineId;
    const sameSpinePos = tocItem.spinePos === locationSpinePos;

    if (tocId && tocId === location.anchorUuid) {
      upsertCandidate(candidateMap, tocItem, 'anchor', 100);
      continue;
    }

    if ((sameSpineId || sameSpinePos) && (
      titlesRoughlyMatch(normalizedTocLabel, normalizedLocationTitle) ||
      titlesRoughlyMatch(normalizedTocLabel, normalizedTopicTitle) ||
      titlesRoughlyMatch(normalizedTocLabel, normalizedCategoryTitle)
    )) {
      upsertCandidate(candidateMap, tocItem, 'spine-title', 80);
      continue;
    }
  }

  return [...candidateMap.values()].sort((left, right) => right.score - left.score);
}

function upsertCandidate(
  candidateMap: Map<string, TopicTocCandidate>,
  tocItem: TocItem,
  matchType: TopicTocMatchType,
  score: number,
): void {
  const candidateKey = `${tocItem.id || 'null'}:${tocItem.spinePos}`;
  const existing = candidateMap.get(candidateKey);

  if (!existing || score > existing.score) {
    candidateMap.set(candidateKey, {
      tocId: tocItem.id,
      tocLabel: tocItem.label,
      spinePos: tocItem.spinePos,
      spineItemId: tocItem.spineItem?.id,
      matchType,
      score,
    });
  }
}

function findNearestCategoryNode(
  topicsTree: BonsaiTree<TagNode | Record<string, unknown>>,
  node: TagNode,
): TagNode | undefined {
  let current: TagNode | undefined = node;

  while (current?.parent) {
    const parent = topicsTree[current.parent] as TagNode | undefined;
    if (!parent) {
      return undefined;
    }

    if (parent.type === TagType.SUBJECT) {
      return current;
    }

    current = parent;
  }

  return undefined;
}

function findParentSubjectNode(
  topicsTree: BonsaiTree<TagNode | Record<string, unknown>>,
  node: TagNode,
): TagNode | undefined {
  let current: TagNode | undefined = node;

  while (current?.parent) {
    const parent = topicsTree[current.parent] as TagNode | undefined;
    if (!parent) {
      return undefined;
    }

    if (parent.type === TagType.SUBJECT) {
      return parent;
    }

    current = parent;
  }

  return undefined;
}

function indexEntries<T extends string | number>(
  entries: TopicTocReverseMapEntry[],
  getKey: (entry: TopicTocReverseMapEntry) => T | undefined,
): Record<T, TopicTocReverseMapEntry[]> {
  const indexedEntries: Record<T, TopicTocReverseMapEntry[]> = Object.create(null);

  entries.forEach((entry) => {
    const key = getKey(entry);
    if (key === undefined || key === null || key === '') {
      return;
    }

    const bucket = indexedEntries[key] || [];
    indexedEntries[key] = [...bucket, entry];
  });

  return indexedEntries;
}

function findStrengthenedSpineFallbackEntries(
  reverseMap: TopicTocReverseMap,
  spineId: string,
  targetCfi?: string,
): TopicTocReverseMapEntry[] {
  const entriesOnSpine = reverseMap.bySpineId[spineId] || [];
  if (!entriesOnSpine.length) {
    return [];
  }

  const sortedEntries = [...entriesOnSpine].sort((left, right) => {
    const positionCmp = compareStepIndexes(
      left.parsedCfi.stepIndexes || [],
      right.parsedCfi.stepIndexes || [],
    );
    if (positionCmp !== 0) {
      return positionCmp;
    }

    return left.topicId.localeCompare(right.topicId);
  });

  const targetStepIndexes = targetCfi ? parseCfiStepIndexes(targetCfi) : [];
  const precedingEntries = targetStepIndexes.length > 0
    ? sortedEntries.filter(
      (entry) => compareStepIndexes(entry.parsedCfi.stepIndexes || [], targetStepIndexes) <= 0,
    )
    : [];

  // No topic on the spine precedes the requested position - this means either no
  // target CFI was given at all, or the target sits at/above the page root (shallower
  // than every real topic anchor). Either way we can't tell which in-page section is
  // "current", so treat the whole spine item as the page: a single XHTML page can
  // legitimately contain multiple topic sections (e.g. two TOC sub-headings on one
  // page), and a page-level query must report every topic on it rather than picking
  // whichever section happens to sort first.
  if (precedingEntries.length === 0) {
    return sortedEntries;
  }

  const selectedEntry = precedingEntries[precedingEntries.length - 1];
  const pageEntries = reverseMap.byPageScopeKey[selectedEntry.pageScopeKey] || [];

  return pageEntries.length > 0 ? pageEntries : [selectedEntry];
}

function parseCfiStepIndexes(cfi: string): number[] {
  const parsed = parseCfi(cfi);
  return (parsed.steps || []).map((step) => step.index);
}

function compareStepIndexes(left: number[], right: number[]): number {
  const max = Math.max(left.length, right.length);
  for (let i = 0; i < max; i += 1) {
    const leftValue = left[i];
    const rightValue = right[i];

    if (leftValue === undefined && rightValue === undefined) {
      return 0;
    }

    if (leftValue === undefined) {
      return -1;
    }

    if (rightValue === undefined) {
      return 1;
    }

    if (leftValue !== rightValue) {
      return leftValue - rightValue;
    }
  }

  return 0;
}

function pickBestEntry(
  entries: TopicTocReverseMapEntry[] | undefined,
  titleHint?: string,
): TopicTocReverseMapEntry | null {
  if (!entries?.length) {
    return null;
  }

  if (!titleHint) {
    return entries[0];
  }

  const normalizedTitleHint = normalizeText(titleHint);
  return entries.find((entry) => entryMatchesTitle(entry, normalizedTitleHint)) || entries[0];
}

function entryMatchesTitle(entry: TopicTocReverseMapEntry, normalizedTitleHint: string): boolean {
  return [
    entry.topicTitle,
    entry.categoryTitle,
    entry.subjectTitle,
    entry.location.title,
    entry.bestCandidate?.tocLabel,
  ].some((value) => titlesRoughlyMatch(normalizeText(value || ''), normalizedTitleHint));
}

function normalizeText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ');
}

function titlesRoughlyMatch(left: string, right: string): boolean {
  if (!left || !right) {
    return false;
  }

  return left === right || left.includes(right) || right.includes(left);
}
